import type { Ref } from 'vue';
import { ref, computed, watch } from 'vue';
import { isValidDate, isValidEmailFormat } from '@/shared/lib';
import type { ValidationFieldError } from './useUserManagement';
import { isDateAfter, isDateBefore } from './userDateRules';

interface UseUserFormValidationParams {
  name: Ref<string>;
  username: Ref<string>;
  email: Ref<string>;
  registrationNumber: Ref<string>;
  admissionDate: Ref<Date | null>;
  dismissalDate: Ref<Date | null>;
  status: Ref<string>;
  isHydratingUserData?: Ref<boolean>;
}

export function useUserFormValidation(params: UseUserFormValidationParams) {
  const serverEmailError = ref('');
  const serverRegistrationNumberError = ref('');
  const serverUsernameError = ref('');
  const isAutoClearingDismissalDate = ref(false);

  watch(params.email, () => {
    serverEmailError.value = '';
  });

  watch(params.registrationNumber, () => {
    serverRegistrationNumberError.value = '';
  });

  watch(params.username, () => {
    serverUsernameError.value = '';
  });

  watch(params.admissionDate, (newAdmissionDate) => {
    if (params.isHydratingUserData?.value) return;

    if (isDateAfter(newAdmissionDate, params.dismissalDate.value)) {
      isAutoClearingDismissalDate.value = true;
      params.dismissalDate.value = null;
      params.status.value = 'ACTIVE';
      return;
    }

    if (isDateAfter(params.dismissalDate.value, newAdmissionDate)) {
      params.status.value = 'INACTIVE';
    }
  });

  watch(params.dismissalDate, (newDismissalDate) => {
    if (params.isHydratingUserData?.value) return;

    if (isAutoClearingDismissalDate.value) {
      isAutoClearingDismissalDate.value = false;
      return;
    }

    if (newDismissalDate === null) {
      params.status.value = 'ACTIVE';
      return;
    }

    if (!isValidDate(newDismissalDate) || !isValidDate(params.admissionDate.value)) return;

    if (isDateAfter(newDismissalDate, params.admissionDate.value)) {
      params.status.value = 'INACTIVE';
      return;
    }

    if (isDateBefore(newDismissalDate, params.admissionDate.value)) {
      isAutoClearingDismissalDate.value = true;
      params.dismissalDate.value = null;
      params.status.value = 'ACTIVE';
    }
  });

  watch(params.status, (newStatus) => {
    if (params.isHydratingUserData?.value) return;

    if (newStatus === 'ACTIVE' && params.dismissalDate.value !== null) {
      params.dismissalDate.value = null;
    }
  });

  const emailFormatError = computed(() => {
    if (!params.email.value) return '';
    return isValidEmailFormat(params.email.value) ? '' : 'Formato de email inválido';
  });

  const emailError = computed(() => {
    if (emailFormatError.value) return emailFormatError.value;
    return serverEmailError.value;
  });

  const admissionDateError = computed(() => {
    if (!params.admissionDate.value) return '';
    if (!isValidDate(params.admissionDate.value)) return 'Data de admissão inválida';
    return '';
  });

  const dismissalDateError = computed(() => {
    if (!params.dismissalDate.value) return '';
    if (!isValidDate(params.dismissalDate.value)) return 'Data de demissão inválida';
    return '';
  });

  const usernameError = computed(() => {
    if (/\s/.test(params.username.value)) return 'O login não pode conter espaços';
    return serverUsernameError.value;
  });

  const registrationNumberError = computed(() => {
    const trimmed = params.registrationNumber.value.trim();
    if (!trimmed) return 'Número de matrícula é obrigatório';
    if (!/^\d{1,6}$/.test(trimmed)) return 'Número de matrícula deve conter de 1 a 6 dígitos';
    return serverRegistrationNumberError.value;
  });

  const isFormValid = computed(() => {
    if (!params.name.value.trim() || params.name.value.trim().length < 3) return false;
    if (!params.registrationNumber.value.trim()) return false;
    if (!/^\d{1,6}$/.test(params.registrationNumber.value.trim())) return false;
    if (!params.username.value.trim() || /\s/.test(params.username.value)) return false;
    if (!isValidEmailFormat(params.email.value)) return false;
    if (!isValidDate(params.admissionDate.value)) return false;
    if (params.status.value === 'INACTIVE' && !isValidDate(params.dismissalDate.value)) {
      return false;
    }
    if (serverRegistrationNumberError.value) return false;
    if (admissionDateError.value || dismissalDateError.value) return false;
    return true;
  });

  const extractFieldErrorMessage = (
    fieldErrors: ValidationFieldError[],
    property: string
  ): string => {
    const fieldError = fieldErrors.find((e) => e.property === property);
    return fieldError?.message ?? '';
  };

  return {
    serverEmailError,
    serverRegistrationNumberError,
    serverUsernameError,
    emailError,
    admissionDateError,
    dismissalDateError,
    usernameError,
    registrationNumberError,
    isFormValid,
    extractFieldErrorMessage,
  };
}
